import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Eye, Zap } from 'lucide-react';

import {
  Alert,
  Badge,
  Button,
  Card,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  EmptyState,
  HelpTip,
  IconByName,
  IconPicker,
  Input,
  Label,
  PageHeader,
  Select,
  Switch,
  useConfirmDialog,
} from '@/components/ui';
import {
  useActivityTypeActions,
  useActivityTypes,
  useActivityTypeVisibility,
  type ActivityCategoria,
  type ActivityType,
  type CreateActivityTypeInput,
  type UpdateActivityTypeInput,
} from '@/lib/activity-types-store';
import { cn } from '@/lib/cn';
import { VisibilityDialog, VisibilityBadge } from './components/VisibilityDialog';

/**
 * /admin/tipos-atividade — gestão unificada de tipos (R5.2 Bloco H).
 *
 * 4 categorias: activity / task / event / note (migration 024 + endpoint
 * Bloco G). Lista agrupada por categoria, cada tipo com ícone + cor +
 * sistema badge. Modal compartilhado pra create + edit.
 *
 * Permissões: backend exige admin pra POST/PUT/DELETE. Tela exibe sempre
 * (read-only pra non-admin) mas botões só aparecem pra admin.
 */

const CATEGORIA_LABELS: Record<ActivityCategoria, string> = {
  activity: 'Registro no histórico',
  task: 'Tarefa',
  event: 'Evento',
  note: 'Nota',
};

const CATEGORIA_ORDER: ActivityCategoria[] = ['task', 'event', 'note', 'activity'];

export function TiposAtividadePage() {
  // FASE 2 / M1: ConfirmDialog
  const confirm = useConfirmDialog();
  const { data: tipos = [], isLoading } = useActivityTypes({ includeInactive: true });
  const { create, update, remove, pending } = useActivityTypeActions();
  const [editing, setEditing] = useState<ActivityType | null>(null);
  const [creatingFor, setCreatingFor] = useState<ActivityCategoria | null>(null);
  const [error, setError] = useState<string | null>(null);
  // U.2 (FEAT-V2-013): dialog de configurar visibilidade do tipo
  const [visibilityFor, setVisibilityFor] = useState<ActivityType | null>(null);

  const byCategoria = useMemo(() => {
    const m = new Map<ActivityCategoria, ActivityType[]>();
    for (const c of CATEGORIA_ORDER) m.set(c, []);
    for (const t of tipos) {
      const arr = m.get(t.categoria) ?? [];
      arr.push(t);
      m.set(t.categoria, arr);
    }
    for (const arr of m.values()) {
      arr.sort((a, b) => a.ordem - b.ordem || a.label.localeCompare(b.label));
    }
    return m;
  }, [tipos]);

  async function handleSave(
    input: CreateActivityTypeInput | UpdateActivityTypeInput
  ): Promise<void> {
    setError(null);
    try {
      if (editing) {
        await update(editing.id, input as UpdateActivityTypeInput);
      } else {
        await create(input as CreateActivityTypeInput);
      }
      setEditing(null);
      setCreatingFor(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Não foi possível salvar o tipo.');
    }
  }

  async function handleDelete(t: ActivityType): Promise<void> {
    if (t.sistema) {
      window.alert('Tipos do sistema não podem ser excluídos — o histórico dos cards depende deles.');
      return;
    }
    const ok = await confirm({
      title: 'Excluir tipo',
      message: `Excluir "${t.label}"? Dá pra reativar depois — nada do histórico é perdido.`,
      confirmLabel: 'Excluir',
      variant: 'destructive',
    });
    if (!ok) return;
    try {
      await remove(t.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Não foi possível excluir o tipo.');
    }
  }

  return (
    <div className="min-w-0 space-y-5" data-testid="admin-tipos-atividade">
      <PageHeader
        crumb="Configurações · Operação"
        title="Tipos de atividade"
        subtitle="Os tipos de tarefa, evento, nota e registro que a equipe pode criar nos cards. Os marcados como “sistema” não podem ser apagados — o histórico depende deles."
      />

      {error && (
        <Alert variant="danger" rail>
          {error}
        </Alert>
      )}

      {isLoading ? (
        <p className="text-sm text-ink-3 dark:text-ink-dark-3 italic">Carregando tipos…</p>
      ) : (
        CATEGORIA_ORDER.map((cat) => {
          const list = byCategoria.get(cat) ?? [];
          return (
            <section key={cat} data-testid={`tipos-section-${cat}`} className="min-w-0">
              <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                <h2 className="font-display text-xl text-ink-1 dark:text-ink-dark-1">
                  {CATEGORIA_LABELS[cat]}
                  <span className="ml-2 text-sm text-ink-3 dark:text-ink-dark-3 tabular-nums">
                    ({list.length})
                  </span>
                </h2>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  data-testid={`tipos-new-${cat}`}
                  onClick={() => setCreatingFor(cat)}
                >
                  + Novo
                </Button>
              </div>

              {list.length === 0 ? (
                <Card variant="flat" className="min-w-0">
                  <EmptyState
                    title={`Nenhum tipo de ${CATEGORIA_LABELS[cat].toLowerCase()}`}
                    description="Use o botão “+ Novo” acima pra criar o primeiro."
                  />
                </Card>
              ) : (
                <ul className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                  {list.map((t) => (
                    <TipoCard
                      key={t.id}
                      tipo={t}
                      onEdit={() => setEditing(t)}
                      onDelete={() => handleDelete(t)}
                      onVisibility={() => setVisibilityFor(t)}
                      busy={pending === t.id}
                    />
                  ))}
                </ul>
              )}
            </section>
          );
        })
      )}

      {(editing || creatingFor) && (
        <TipoFormDialog
          editing={editing}
          defaultCategoria={creatingFor ?? editing?.categoria ?? 'task'}
          onCancel={() => {
            setEditing(null);
            setCreatingFor(null);
          }}
          onSave={handleSave}
          busy={pending === 'create' || (editing && pending === editing.id) || false}
        />
      )}

      {/* U.2 (FEAT-V2-013): Modal de visibilidade do tipo */}
      {visibilityFor && (
        <VisibilityDialog
          open
          onClose={() => setVisibilityFor(null)}
          activityType={visibilityFor}
        />
      )}
    </div>
  );
}

// ─── Card de tipo ──────────────────────────────────────────────────────

function TipoCard({
  tipo,
  onEdit,
  onDelete,
  onVisibility,
  busy,
}: {
  tipo: ActivityType;
  onEdit: () => void;
  onDelete: () => void;
  onVisibility: () => void;
  busy: boolean;
}) {
  // U.2: lê visibility scopes pra renderizar badge resumida.
  // Hook só "ativa" quando o tipo é montado — sem custo extra (cache RQ).
  const { data: vis } = useActivityTypeVisibility(tipo.id);
  return (
    <li
      data-testid={`tipo-card-${tipo.id}`}
      className={cn(
        'flex items-center gap-3 p-3 rounded-xl border border-line dark:border-line-dark bg-paper-surface dark:bg-paper-dark-surface',
        !tipo.ativo && 'opacity-50'
      )}
    >
      <div
        className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
        style={{
          backgroundColor: tipo.cor ? `${tipo.cor}22` : undefined,
          color: tipo.cor ?? undefined,
        }}
      >
        <IconByName name={tipo.icone} className="w-4 h-4" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <p className="text-sm font-medium text-ink-1 dark:text-ink-dark-1 truncate">
            {tipo.label}
          </p>
          {tipo.sistema && (
            <Badge
              variant="neutral"
              size="sm"
              title="Tipo do sistema — o histórico depende dele, então não pode ser apagado"
            >
              Sistema
            </Badge>
          )}
          {!tipo.ativo && (
            <Badge variant="danger" size="sm">
              Inativo
            </Badge>
          )}
          {/* U.2 (FEAT-V2-013): badge resumo de visibilidade */}
          {vis?.scopes && <VisibilityBadge scopes={vis.scopes} />}
        </div>
        <p className="text-xs text-ink-3 dark:text-ink-dark-3 truncate">
          {tipo.slug}
          {tipo.workflowSlug ? ` · ${tipo.workflowSlug}` : ''}
        </p>
      </div>
      <div className="flex shrink-0 flex-wrap items-center gap-1">
        {/* FASE-C: emoji ⚙ / 👁 viraram ícones Lucide com nome acessível. */}
        <Link
          to={`/admin/tipos-atividade/${tipo.id}/automacoes`}
          data-testid={`tipo-automacoes-${tipo.id}`}
          className="inline-flex items-center px-2 py-1 rounded-md text-ink-2 dark:text-ink-dark-2 hover:bg-paper dark:hover:bg-paper-dark-warm transition-colors"
          title="Configurar as automações deste tipo"
          aria-label={`Automações de ${tipo.label}`}
        >
          <Zap className="w-4 h-4" aria-hidden="true" />
        </Link>
        {/* U.2: botão Visibilidade abre modal com 4 escopos */}
        <Button
          type="button"
          size="sm"
          variant="ghost"
          data-testid={`tipo-visibility-${tipo.id}`}
          onClick={onVisibility}
          title="Escolher quem vê este tipo"
          aria-label={`Quem vê o tipo ${tipo.label}`}
        >
          <Eye className="w-4 h-4" aria-hidden="true" />
        </Button>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          data-testid={`tipo-edit-${tipo.id}`}
          onClick={onEdit}
          disabled={busy}
        >
          Editar
        </Button>
        {!tipo.sistema && (
          <Button
            type="button"
            size="sm"
            variant="destructive"
            data-testid={`tipo-delete-${tipo.id}`}
            onClick={onDelete}
            disabled={busy || !tipo.ativo}
          >
            Excluir
          </Button>
        )}
      </div>
    </li>
  );
}

// ─── Form dialog ───────────────────────────────────────────────────────

function TipoFormDialog({
  editing,
  defaultCategoria,
  onCancel,
  onSave,
  busy,
}: {
  editing: ActivityType | null;
  defaultCategoria: ActivityCategoria;
  onCancel: () => void;
  onSave: (input: CreateActivityTypeInput | UpdateActivityTypeInput) => Promise<void>;
  busy: boolean;
}) {
  const [slug, setSlug] = useState(editing?.slug ?? '');
  const [label, setLabel] = useState(editing?.label ?? '');
  const [categoria, setCategoria] = useState<ActivityCategoria>(
    editing?.categoria ?? defaultCategoria
  );
  const [icone, setIcone] = useState<string | null>(editing?.icone ?? null);
  const [cor, setCor] = useState<string>(editing?.cor ?? '#3b82f6');
  const [workflowSlug, setWorkflowSlug] = useState<string>(editing?.workflowSlug ?? '');
  const [ativo, setAtivo] = useState(editing?.ativo ?? true);
  const [exigeDescricao, setExigeDescricao] = useState(editing?.exigeDescricao ?? false);
  // M-1 (FEAT-V2-029 / HK-1): toggle "tipo agendável" — modal unificado
  // delega CreateTaskDialog quando true; POST /atividades simples quando false.
  const [requiresSchedule, setRequiresSchedule] = useState(
    editing?.requiresSchedule ?? false,
  );

  const isEdit = !!editing;
  const isSystem = editing?.sistema === true;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (isEdit) {
      // PUT só permite update de label/icone/cor/ordem/ativo/exigeDescricao
      const update: UpdateActivityTypeInput = {
        label,
        icone: icone || null,
        cor: cor || null,
        ativo,
        exigeDescricao,
        requiresSchedule,
      };
      await onSave(update);
    } else {
      const create: CreateActivityTypeInput = {
        slug,
        label,
        categoria,
        icone: icone || null,
        cor: cor || null,
        workflowSlug: workflowSlug || null,
        exigeDescricao,
        requiresSchedule,
      };
      await onSave(create);
    }
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onCancel()} size="md">
      <DialogHeader>
        <DialogTitle>{isEdit ? `Editar "${editing!.label}"` : 'Novo tipo'}</DialogTitle>
      </DialogHeader>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          <div className="space-y-3">
            {!isEdit && (
              <>
                <div>
                  <Label htmlFor="tipo-slug" required>
                    <span className="inline-flex items-center gap-1">
                      Identificador
                      <HelpTip texto="Nome curto que o sistema usa por baixo dos panos. Você precisa dele quando aponta uma automação pra este tipo. Não muda depois que o tipo é criado." />
                    </span>
                  </Label>
                  <Input
                    id="tipo-slug"
                    data-testid="tipo-form-slug"
                    value={slug}
                    onChange={(e) => setSlug(e.target.value)}
                    placeholder="ex: ligacao_retorno"
                    hint="Só letras minúsculas, números e _ (underline). Não muda depois de criado."
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="tipo-categoria" required>
                    Categoria
                  </Label>
                  <Select
                    id="tipo-categoria"
                    value={categoria}
                    onChange={(e) => setCategoria(e.target.value as ActivityCategoria)}
                  >
                    <option value="task">Tarefa</option>
                    <option value="event">Evento</option>
                    <option value="note">Nota</option>
                    <option value="activity">Registro no histórico</option>
                  </Select>
                </div>
              </>
            )}

            <div>
              <Label htmlFor="tipo-label" required>
                Nome
              </Label>
              <Input
                id="tipo-label"
                data-testid="tipo-form-label"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder="ex: Ligação de retorno"
                hint="É este nome que a equipe vê ao criar a atividade."
                required
              />
            </div>

            {!isEdit && (
              <div>
                <Label htmlFor="tipo-workflow">Fluxo de trabalho (opcional)</Label>
                <Input
                  id="tipo-workflow"
                  value={workflowSlug}
                  onChange={(e) => setWorkflowSlug(e.target.value)}
                  placeholder="Ex: manutencao"
                  hint="Deixe vazio pra este tipo valer em todos os fluxos. Preenchido, ele só aparece no fluxo indicado."
                />
              </div>
            )}

            <div>
              <Label>Ícone</Label>
              <IconPicker value={icone} onChange={setIcone} />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="min-w-0">
                <Label htmlFor="tipo-cor">Cor</Label>
                <Input
                  id="tipo-cor"
                  type="color"
                  value={cor}
                  onChange={(e) => setCor(e.target.value)}
                />
              </div>
              <div className="flex min-w-0 items-end gap-2 pb-2">
                <Switch
                  id="tipo-exige-desc"
                  checked={exigeDescricao}
                  onChange={(e) => setExigeDescricao(e.target.checked)}
                />
                <Label htmlFor="tipo-exige-desc" className="text-xs">
                  Exigir descrição
                </Label>
              </div>
            </div>

            {/* M-1 (FEAT-V2-029 / HK-1): toggle "tipo com prazo". Quando ON,
                modal unificado +Nova atividade delega CreateTaskDialog
                (Calendar sync + invitations). Quando OFF, é log simples no
                histórico do card. */}
            <div className="flex min-w-0 items-start gap-2">
              <Switch
                id="tipo-requires-schedule"
                data-testid="tipo-form-requires-schedule"
                checked={requiresSchedule}
                onChange={(e) => setRequiresSchedule(e.target.checked)}
              />
              <div className="min-w-0">
                <Label htmlFor="tipo-requires-schedule" className="text-xs">
                  Este tipo tem data e hora marcadas
                </Label>
                <p className="text-xs text-ink-3 dark:text-ink-dark-3 mt-0.5">
                  Ligado, ao criar a atividade a equipe escolhe data e hora e o
                  compromisso vai pro Google Calendar. Desligado, a atividade só entra no
                  histórico do card.
                </p>
              </div>
            </div>

            {isEdit && !isSystem && (
              <div className="flex items-center gap-2">
                <Switch
                  id="tipo-ativo"
                  checked={ativo}
                  onChange={(e) => setAtivo(e.target.checked)}
                />
                <Label htmlFor="tipo-ativo">Ativo</Label>
              </div>
            )}

            {isSystem && (
              <Alert variant="info" rail>
                Este é um tipo do sistema. Você pode trocar o nome, o ícone e a cor —
                o identificador e a categoria continuam como estão, porque o histórico
                dos cards depende deles.
              </Alert>
            )}
          </div>
        </DialogContent>
        <DialogFooter>
          <Button type="button" variant="ghost" onClick={onCancel} disabled={busy}>
            Cancelar
          </Button>
          <Button type="submit" loading={busy} disabled={!label || (!isEdit && !slug)}>
            {isEdit ? 'Salvar' : 'Criar'}
          </Button>
        </DialogFooter>
      </form>
    </Dialog>
  );
}
